/* eslint-disable */
import React, { useEffect, useState, useRef } from 'react';
import DataService from '../../services/data.service';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import LoaderOverlay from '../common/loader.component';
import DataTable from 'react-data-table-component';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faCalendarPlus,
  faCalendarDay,
  faDollarSign,
  faUser,
  faBan,
  faClipboardCheck,
  faChartLine,
  faUserClock,
  faTrendUp,
  faTrendDown,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

// Modern color palette
const colorPalette = {
  primary: {
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    solid: '#667eea',
    light: '#e0e7ff'
  },
  success: {
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    solid: '#10b981',
    light: '#d1fae5'
  },
  warning: {
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    solid: '#f59e0b',
    light: '#fef3c7'
  },
  danger: {
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    solid: '#ef4444',
    light: '#fee2e2'
  },
  info: {
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    solid: '#3b82f6',
    light: '#dbeafe'
  },
  purple: {
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    solid: '#8b5cf6',
    light: '#ede9fe'
  },
  pink: {
    gradient: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
    solid: '#ec4899',
    light: '#fce7f3'
  },
  teal: {
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    solid: '#14b8a6',
    light: '#ccfbf1'
  }
};

const statsConfig = [
  { key: 'totalBookings', title: 'Total Bookings', icon: faCalendarPlus, color: 'primary' },
  { key: 'confirmedBookings', title: 'Confirmed', icon: faCalendarCheck, color: 'success' },
  { key: 'openBookings', title: 'Pending', icon: faUserClock, color: 'warning' },
  { key: 'completedBookings', title: 'Completed', icon: faClipboardCheck, color: 'teal' },
  { key: 'cancelledBookings', title: 'Cancelled', icon: faBan, color: 'danger' },
  { key: 'totalRevenue', title: 'Total Revenue', icon: faDollarSign, color: 'purple', prefix: '$' },
  { key: 'todayRevenue', title: "Today's Revenue", icon: faChartLine, color: 'pink', prefix: '$' },
];

// Safe date helpers
const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
const parseYMD = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!m) return null;
  const y = Number(m[1]), mm = Number(m[2]), d = Number(m[3]);
  if (mm < 1 || mm > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mm - 1, d, 0, 0, 0, 0);
  return isFinite(dt.getTime()) ? dt : null;
};

const Dashboard = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [stats, setStats] = useState({
    totalBookings: 0,
    openBookings: 0,
    confirmedBookings: 0,
    cancelledBookings: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    completedBookings: 0,
    totalVisitors: 0
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [visitors, setVisitors] = useState({ today: 0, total: 0 });
  const [visitorLocations, setVisitorLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const calendarRef = useRef(null);
  const navigate = useNavigate();

  // Generate year options (last 5 years)
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchDashboardStats();
    fetchMonthlyBookings();
    fetchVisitorStats();
    fetchVisitorLocations();
    fetchConfirmedBookings();
  }, [selectedYear]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeTooltip) {
        setActiveTooltip(null);
      }
    };

    if (activeTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeTooltip]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await DataService.getDashboardStats(selectedYear);
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyBookings = async () => {
    try {
      const response = await DataService.getMonthlyBookings(selectedYear);
      setMonthlyData(response.data || []);
    } catch (error) {
      console.error('Error fetching monthly booking data:', error);
    }
  };

  const fetchVisitorStats = async () => {
    try {
      const response = await DataService.getVisitorStats();
      setVisitors({
        today: response.data?.today_unique_visitors || 0,
        total: response.data?.total_unique_visitors || 0,
      });
    } catch (error) {
      console.error('Error fetching visitor stats:', error);
    }
  };

  const fetchVisitorLocations = async () => {
    try {
      const response = await DataService.getVisitorLocations();
      setVisitorLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching visitor locations:', error);
    }
  };

  const fetchConfirmedBookings = async () => {
    try {
      setLoading(true);
      const [bookingsResponse, blockedResponse] = await Promise.all([
        DataService.getConfirmedBookings(),
        DataService.getBlockedDates()
      ]);

      const bookings = bookingsResponse?.data || [];
      const blockedDates = blockedResponse?.data || [];

      const formattedBookings = bookings
        .filter(booking => booking?.status === "confirmed")
        .map((booking) => {
          try {
            const totalDuration = (booking.services || []).reduce((sum, s) =>
              sum + (parseInt(s?.service_duration || 0, 10) || 0), 0
            );

            const baseDate = parseYMD(booking.date);
            if (!baseDate) return null;

            const [hour = 0, minute = 0] = (booking.time || '00:00').split(':').map(Number);
            const start = new Date(baseDate);
            start.setHours(hour, minute, 0, 0);

            const end = new Date(start);
            end.setMinutes(start.getMinutes() + totalDuration);

            if (!isValidDate(start) || !isValidDate(end)) return null;

            return {
              id: booking.booking_id,
              title: `✓ ${booking.name}`,
              start: start.toISOString(),
              end: end.toISOString(),
              backgroundColor: '#10b981',
              borderColor: '#059669',
              textColor: '#ffffff',
              extendedProps: {
                type: 'booking',
                bookingData: booking
              }
            };
          } catch (err) {
            return null;
          }
        })
        .filter(Boolean);

      // Parse blocked dates with start/end times
      const parsedBlocks = blockedDates
        .map((block) => {
          try {
            const baseDate = parseYMD(block.date);
            if (!baseDate) return null;

            let start, end;
            if (block.time === 'All') {
              start = new Date(baseDate);
              start.setHours(10, 0, 0, 0);
              end = new Date(baseDate);
              end.setHours(19, 0, 0, 0);
            } else {
              const [hour = 0, minute = 0] = (block.time || '00:00').split(':').map(Number);
              start = new Date(baseDate);
              start.setHours(hour, minute, 0, 0);
              end = new Date(start);
              end.setMinutes(start.getMinutes() + (Number(block.duration) || 30));
            }

            if (!isValidDate(start) || !isValidDate(end)) return null;

            return { start, end, reason: block.reason || 'No reason', id: block.id };
          } catch (err) {
            return null;
          }
        })
        .filter(Boolean);

      // Group by date and reason, then merge overlapping/consecutive ranges
      const groupedByDateAndReason = {};
      parsedBlocks.forEach((block) => {
        const dateKey = block.start.toDateString();
        const reasonKey = block.reason || 'No reason';
        const groupKey = `${dateKey}_${reasonKey}`;

        if (!groupedByDateAndReason[groupKey]) {
          groupedByDateAndReason[groupKey] = [];
        }
        groupedByDateAndReason[groupKey].push(block);
      });

      // Merge blocks with same date and reason
      const mergedBlocks = [];
      Object.values(groupedByDateAndReason).forEach((blocks) => {
        // Sort by start time
        blocks.sort((a, b) => a.start - b.start);

        const merged = [];
        let current = blocks[0];

        for (let i = 1; i < blocks.length; i++) {
          const next = blocks[i];
          // Check if overlapping or consecutive (within 15 minutes)
          if (next.start <= new Date(current.end.getTime() + 15 * 60000)) {
            // Merge: extend current end time if next ends later
            if (next.end > current.end) {
              current.end = next.end;
            }
          } else {
            // No overlap, save current and move to next
            merged.push(current);
            current = next;
          }
        }
        merged.push(current);
        mergedBlocks.push(...merged);
      });

      // Format merged blocks for calendar
      const formattedBlocked = mergedBlocks.map((block, index) => ({
        id: `blocked-${block.id || index}`,
        title: `🚫 Blocked`,
        start: block.start.toISOString(),
        end: block.end.toISOString(),
        backgroundColor: '#6b7280',
        borderColor: '#4b5563',
        textColor: '#ffffff',
        extendedProps: {
          type: 'blocked',
          reason: block.reason
        }
      }));

      setEvents([...formattedBookings, ...formattedBlocked]);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventClick = (clickInfo) => {
    if (clickInfo.event.extendedProps.type === 'booking') {
      const bookingData = clickInfo.event.extendedProps.bookingData;
      if (bookingData) navigate('/booking-details', { state: { booking: bookingData } });
    }
  };

  // Booking status distribution for doughnut chart
  const bookingDistribution = {
    labels: ['Confirmed', 'Pending', 'Cancelled', 'Completed'],
    datasets: [{
      data: [
        stats.confirmedBookings,
        stats.openBookings,
        stats.cancelledBookings,
        stats.completedBookings
      ],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444', '#14b8a6'],
      borderWidth: 0,
      hoverOffset: 10
    }]
  };

  // Monthly bookings bar chart
  const monthlyChartData = {
    labels: monthlyData.map(item => item.month),
    datasets: [{
      label: 'Total Bookings',
      data: monthlyData.map(item => item.total),
      backgroundColor: 'rgba(102, 126, 234, 0.8)',
      borderRadius: 8,
      borderSkipped: false,
    }]
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        borderRadius: 8,
        titleFont: { size: 14, weight: 'bold' },
        bodyFont: { size: 13 }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { font: { size: 12 } }
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 12 } }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: { size: 12, weight: '500' },
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        borderRadius: 8
      }
    },
    cutout: '70%'
  };

  return (
    <>
      <LoaderOverlay loading={loading} />
      <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: '2rem 0' }}>
        <div className="container-fluid">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1" style={{ fontWeight: 700, color: '#1e293b' }}>
                Dashboard Overview
              </h2>
              <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                Welcome back! Here's what's happening with your salon.
              </p>
            </div>
            <div className="text-end d-flex flex-column align-items-end gap-2">
              <select
                className="form-select form-select-sm"
                style={{
                  width: 'auto',
                  borderRadius: '10px',
                  border: '2px solid #e2e8f0',
                  fontWeight: 600,
                  color: '#1e293b',
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }}
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="row g-3 g-lg-4 mb-5">
            {statsConfig.map((config) => (
              <StatCard
                key={config.key}
                title={config.title}
                value={stats[config.key] || 0}
                icon={config.icon}
                color={colorPalette[config.color]}
                prefix={config.prefix}
              />
            ))}
          </div>

          {/* Calendar Section */}
          <div className="row g-4 mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                <div
                  className="card-header text-white d-flex justify-content-between align-items-center"
                  style={{
                    background: colorPalette.primary.gradient,
                    padding: '1.25rem 1.5rem',
                    border: 'none'
                  }}
                >
                  <div>
                    <h5 className="mb-1 fw-bold">📅 Weekly Schedule</h5>
                    <small style={{ opacity: 0.9 }}>View and manage your appointments</small>
                  </div>
                  <div className="d-flex gap-3 align-items-center" style={{ fontSize: '0.875rem' }}>
                    <span className="d-flex align-items-center gap-2">
                      <span style={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#10b981',
                        borderRadius: '50%',
                        boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)'
                      }}></span>
                      <span>Confirmed</span>
                    </span>
                    <span className="d-flex align-items-center gap-2">
                      <span style={{
                        width: 12,
                        height: 12,
                        backgroundColor: '#6b7280',
                        borderRadius: '50%',
                        boxShadow: '0 0 0 3px rgba(107, 114, 128, 0.2)'
                      }}></span>
                      <span>Blocked</span>
                    </span>
                  </div>
                </div>
                <div className="card-body p-0" style={{ backgroundColor: '#ffffff' }}>
                  <CalendarStyles />
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    headerToolbar={{
                      left: 'prev,next today',
                      center: 'title',
                      right: ''
                    }}
                    events={events}
                    eventClick={handleEventClick}
                    slotMinTime="10:00:00"
                    slotMaxTime="19:00:00"
                    slotDuration="00:15:00"
                    slotLabelInterval="00:30:00"
                    height="700px"
                    allDaySlot={false}
                    nowIndicator={true}
                    firstDay={1}
                    slotLabelFormat={{
                      hour: 'numeric',
                      minute: '2-digit',
                      meridiem: 'short'
                    }}
                    eventOverlap={false}
                    slotEventOverlap={false}
                    eventDidMount={(info) => {
                      info.el.style.cursor = 'pointer';
                    }}
                    eventContent={(eventInfo) => {
                      const isBlocked = eventInfo.event.extendedProps?.type === 'blocked';
                      const reason = eventInfo.event.extendedProps?.reason;
                      const bookingData = eventInfo.event.extendedProps?.bookingData;
                      const eventId = eventInfo.event.id;
                      const isTooltipActive = activeTooltip === eventId;

                      const handleInfoClick = (e) => {
                        e.stopPropagation();
                        setActiveTooltip(isTooltipActive ? null : eventId);
                      };

                      return (
                        <div style={{
                          padding: '6px 8px',
                          overflow: 'visible',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          position: 'relative'
                        }}>
                          {/* Info icon for all events */}
                          <div
                            onClick={handleInfoClick}
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '6px',
                              fontSize: '12px',
                              opacity: isTooltipActive ? 1 : 0.8,
                              backgroundColor: isTooltipActive
                                ? 'rgba(255,255,255,0.4)'
                                : 'rgba(255,255,255,0.25)',
                              borderRadius: '50%',
                              width: '18px',
                              height: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              color: '#ffffff',
                              border: '1px solid rgba(255,255,255,0.3)',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              zIndex: 1002
                            }}
                          >
                            ℹ
                          </div>

                          {/* Backdrop overlay */}
                          {isTooltipActive && (
                            <div
                              onClick={handleInfoClick}
                              style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 99998,
                                animation: 'fadeIn 0.3s ease'
                              }}
                            />
                          )}

                          {/* Stylish Tooltip - Fixed positioning */}
                          {isTooltipActive && (
                            <div
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                position: 'fixed',
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                background: isBlocked
                                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                padding: '16px 20px',
                                borderRadius: '16px',
                                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
                                minWidth: '280px',
                                maxWidth: '350px',
                                zIndex: 99999,
                                animation: 'tooltipSlideIn 0.3s ease',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <div style={{
                                fontSize: '15px',
                                fontWeight: 700,
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span>{eventInfo.event.title}</span>
                              </div>

                              {isBlocked && reason && (
                                <div style={{
                                  fontSize: '13px',
                                  marginBottom: '10px',
                                  paddingLeft: '12px',
                                  borderLeft: '3px solid rgba(255, 255, 255, 0.6)',
                                  lineHeight: '1.5'
                                }}>
                                  <strong>Reason:</strong> {reason}
                                </div>
                              )}

                              {!isBlocked && bookingData && (
                                <>
                                  <div style={{
                                    fontSize: '12px',
                                    marginBottom: '8px',
                                    paddingLeft: '12px',
                                    borderLeft: '3px solid rgba(255, 255, 255, 0.6)'
                                  }}>
                                    <strong>Customer:</strong> {bookingData.name}
                                  </div>
                                  {bookingData.services && bookingData.services.length > 0 && (
                                    <div style={{
                                      fontSize: '12px',
                                      marginBottom: '8px',
                                      paddingLeft: '12px',
                                      borderLeft: '3px solid rgba(255, 255, 255, 0.6)'
                                    }}>
                                      <strong>Services:</strong>
                                      <div style={{ marginTop: '4px', fontSize: '11px' }}>
                                        {bookingData.services.map((s, i) => (
                                          <div key={i}>• {s.service_name}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              <div style={{
                                fontSize: '12px',
                                opacity: 0.95,
                                fontWeight: 600,
                                marginTop: '10px',
                                paddingTop: '10px',
                                borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                              }}>
                                <strong>Time:</strong> {eventInfo.timeText}
                              </div>

                              {/* Close button */}
                              <div
                                onClick={handleInfoClick}
                                style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  cursor: 'pointer',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  fontWeight: 'bold',
                                  transition: 'all 0.2s'
                                }}
                              >
                                ×
                              </div>
                            </div>
                          )}

                          <div style={{
                            fontWeight: 600,
                            fontSize: '12px',
                            marginBottom: '3px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            paddingRight: '24px'
                          }}>
                            {eventInfo.event.title}
                          </div>

                          {isBlocked && reason && (
                            <div style={{
                              fontSize: '11px',
                              opacity: 0.95,
                              fontStyle: 'italic',
                              marginBottom: '3px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: '24px'
                            }}>
                              {reason}
                            </div>
                          )}

                          <div style={{
                            fontSize: '10px',
                            opacity: 0.9,
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {eventInfo.timeText}
                          </div>
                        </div>
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="row g-4 mb-4">
            {/* Monthly Bookings Chart */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h5 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                        Monthly Bookings
                      </h5>
                      <small className="text-muted">Year {selectedYear} performance</small>
                    </div>
                    <div
                      style={{
                        backgroundColor: colorPalette.primary.light,
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        color: colorPalette.primary.solid
                      }}
                    >
                      {selectedYear}
                    </div>
                  </div>
                  <div style={{ height: '300px' }}>
                    <Bar data={monthlyChartData} options={monthlyChartOptions} />
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Distribution */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                  <h5 className="mb-1 fw-bold" style={{ color: '#1e293b' }}>
                    Booking Status
                  </h5>
                  <small className="text-muted mb-4 d-block">Distribution overview</small>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Doughnut data={bookingDistribution} options={doughnutOptions} />
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -80%)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                        {stats.totalBookings}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                        Total
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visitor Locations */}
          <div className="row g-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                <div className="card-body p-4">
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      style={{
                        fontSize: '1.25rem',
                        color: colorPalette.success.solid,
                        backgroundColor: colorPalette.success.light,
                        padding: '10px',
                        borderRadius: '10px'
                      }}
                    />
                    <div>
                      <h5 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
                        Visitor Locations
                      </h5>
                      <small className="text-muted">Geographic distribution of visitors</small>
                    </div>
                  </div>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {visitorLocations.length > 0 ? (
                      <DataTable
                        columns={[
                          {
                            name: 'City',
                            selector: row => row.city || 'Unknown',
                            sortable: true,
                            style: { fontWeight: 500 }
                          },
                          {
                            name: 'Country',
                            selector: row => row.country || 'Unknown',
                            sortable: true
                          },
                          {
                            name: 'Visitors',
                            selector: row => row.count,
                            sortable: true,
                            right: true,
                            cell: row => (
                              <span style={{
                                backgroundColor: colorPalette.info.light,
                                color: colorPalette.info.solid,
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '0.875rem'
                              }}>
                                {row.count}
                              </span>
                            )
                          }
                        ]}
                        data={visitorLocations}
                        pagination
                        paginationPerPage={5}
                        highlightOnHover
                        customStyles={{
                          headRow: {
                            style: {
                              backgroundColor: '#f8fafc',
                              borderBottom: '2px solid #e2e8f0',
                              fontWeight: 600,
                              fontSize: '0.875rem'
                            }
                          },
                          rows: {
                            style: {
                              fontSize: '0.875rem',
                              '&:hover': {
                                backgroundColor: '#f8fafc'
                              }
                            }
                          }
                        }}
                      />
                    ) : (
                      <div className="text-center py-5">
                        <p className="text-muted mb-0">No visitor data available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Modern Stat Card Component
const StatCard = ({ title, value, icon, color, prefix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const numericValue = typeof value === 'number' ? value : parseInt(String(value).replace(/\D/g, ''), 10) || 0;
    let start = 0;
    const duration = 1000;
    const increment = numericValue / 50;
    const stepTime = duration / 50;

    const timer = setInterval(() => {
      start += increment;
      if (start >= numericValue) {
        clearInterval(timer);
        setDisplayValue(numericValue);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, stepTime);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className="col-xxl-3 col-xl-3 col-lg-6 col-md-6 col-sm-6 mb-3 mb-lg-0">
      <div
        className="card border-0 shadow-sm h-100"
        style={{
          borderRadius: '16px',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-5px)' : 'translateY(0)',
          cursor: 'default',
          boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.08)'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div
              style={{
                width: '48px',
                height: '48px',
                background: color.gradient,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 8px 16px ${color.solid}40`
              }}
            >
              <FontAwesomeIcon icon={icon} style={{ color: 'white', fontSize: '1.25rem' }} />
            </div>
          </div>
          <div>
            <h3 className="mb-1" style={{ fontWeight: 700, color: '#1e293b', fontSize: '1.75rem' }}>
              {prefix}{displayValue.toLocaleString()}
            </h3>
            <p className="mb-0 text-muted" style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {title}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Styles Component
const CalendarStyles = () => (
  <style>
    {`
      .fc {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
      .fc-theme-standard .fc-scrollgrid { border: none; }
      .fc-col-header-cell {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none !important;
        padding: 12px 8px;
        font-weight: 600;
        font-size: 13px;
        color: white;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      .fc-timegrid-slot {
        height: 40px;
        border-color: #f1f5f9;
      }
      .fc-timegrid-slot-minor { border-top-style: dashed; }
      .fc .fc-timegrid-col.fc-day-today {
        background-color: #fef3c7 !important;
      }
      .fc-event {
        border-radius: 8px;
        border-width: 2px;
        padding: 4px 8px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        box-shadow: 0 2px 4px rgba(0,0,0,0.08);
      }
      .fc-event:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 1000 !important;
      }
      .fc-timegrid-event {
        overflow: visible !important;
      }
      @keyframes tooltipSlideIn {
        from {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.9);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1);
        }
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      .fc .fc-button-primary {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        font-weight: 500;
        padding: 8px 16px;
        border-radius: 8px;
        transition: all 0.2s;
        font-size: 0.875rem;
      }
      .fc .fc-button-primary:hover:not(:disabled) {
        background: linear-gradient(135deg, #5568d3 0%, #6a4193 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
      }
      .fc .fc-button-primary:disabled {
        background: #e2e8f0;
        opacity: 0.6;
      }
      .fc-toolbar-title {
        font-size: 1.375rem;
        font-weight: 700;
        color: #1e293b;
      }
      .fc-timegrid-axis-cushion {
        color: #64748b;
        font-size: 12px;
        font-weight: 500;
      }
      .fc-timegrid-now-indicator-line {
        border-color: #ef4444;
        border-width: 2px;
      }
    `}
  </style>
);

export default Dashboard;
